import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Check, ChevronDown, Dumbbell, Home, Sparkles, Square, Timer, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { toast } from 'sonner'

import {
  addSet,
  evaluateSession,
  getExerciseHistory,
  getSession,
  getSessions,
  getTemplates,
  updateSession,
  updateTemplate,
} from '@/api/services/workout'
import type {
  ExerciseHistoryPoint,
  TemplateItem,
  WorkoutAiEvaluation,
  WorkoutSession,
  WorkoutSetRecord,
} from '@/api/types/workout'
import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { confirmDialog } from '@/lib/confirm-dialog'
import { parseUtcDate } from '@/lib/date-utils'
import { playBeep, useWorkoutStore, vibrate } from '@/store/useWorkoutStore'

import { MuscleSummaryCard } from './muscle-summary-card'

const formatSeconds = (sec: number): string => {
  const sign = sec < 0 ? '-' : ''
  const abs = Math.abs(sec)
  const m = Math.floor(abs / 60)
  const s = Math.floor(abs % 60)
  return `${sign}${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

const RPE_OPTIONS = [
  { value: '6', description: 'かなり余裕' },
  { value: '7', description: 'あと3回できた' },
  { value: '8', description: 'あと2回できた' },
  { value: '9', description: 'あと1回できたかも' },
  { value: '10', description: '限界' },
] as const

type Plan = {
  // スーパーセットのように同じ種目が複数行あるテンプレートで、行を一意に識別するためのキー
  key: string
  exercise_id: number
  exercise_name: string
  exercise_notes: string | null
  target_sets: number
  target_reps: number
  target_weight_kg: number | null
  rest_seconds: number
  // 同じ種目の先行行ぶんのセット数。DB の set_index（種目内の通し番号）から行の進捗を割り出すのに使う
  set_offset: number
}

const buildPlanFromTemplate = (items: TemplateItem[]): Plan[] => {
  const offsets = new Map<number, number>()
  return items
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((i) => {
      // セット数・レップ数はテンプレートでの指定をそのまま使う。AIプラン（テンプレート横断）は
      // テンプレートに値が無いものを補うだけに留める
      const ai = i.latest_ai_plan
      const setOffset = offsets.get(i.exercise_id) ?? 0
      offsets.set(i.exercise_id, setOffset + i.target_sets)
      return {
        key: `item-${i.id}`,
        exercise_id: i.exercise_id,
        exercise_name: i.exercise_name,
        exercise_notes: i.exercise_notes,
        target_sets: i.target_sets,
        target_reps: i.target_reps,
        target_weight_kg: i.target_weight_kg ?? ai?.weight_kg ?? null,
        rest_seconds: i.rest_seconds ?? ai?.rest_seconds ?? 90,
        set_offset: setOffset,
      }
    })
}

const buildPlanFromSets = (sets: WorkoutSetRecord[]): Plan[] => {
  const map = new Map<number, Plan>()
  for (const s of sets) {
    if (!map.has(s.exercise_id)) {
      map.set(s.exercise_id, {
        key: `exercise-${s.exercise_id}`,
        exercise_id: s.exercise_id,
        exercise_name: s.exercise_name,
        exercise_notes: null,
        target_sets: 3,
        target_reps: s.reps ?? 8,
        target_weight_kg: s.weight_kg,
        rest_seconds: 90,
        set_offset: 0,
      })
    }
  }
  return [...map.values()]
}

export const SessionRunnerPage = () => {
  const navigate = useNavigate()
  const params = useParams()
  const sessionId = Number(params.session_id)

  const [session, setSession] = useState<WorkoutSession | null>(null)
  const [plan, setPlan] = useState<Plan[]>([])
  // 同テンプレートの過去セッション（新しい順）。種目ごとに一番近い履歴/AIアドバイスを引くために複数保持する。
  const [prevSessions, setPrevSessions] = useState<WorkoutSession[]>([])
  const [exerciseHistory, setExerciseHistory] = useState<ExerciseHistoryPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [evaluating, setEvaluating] = useState(false)

  const { setStartedAt, restStartedAt, restTargetSeconds, bindSession, startSet, startRest, cancelRest, reset } =
    useWorkoutStore()
  const [tick, setTick] = useState(0) // 250msごとに増えてタイマー再描画
  const [nowMs, setNowMs] = useState(() => Date.now())

  // 入力値
  const [weight, setWeight] = useState<string>('')
  const [reps, setReps] = useState<string>('')
  // RPE選択ダイアログ用
  const [pendingFailed, setPendingFailed] = useState(false)
  const [rpeDialogOpen, setRpeDialogOpen] = useState(false)
  // 手動で選択したプラン行（未開始かつ1セット目のときのみ切替可能）
  const [selectedPlanKey, setSelectedPlanKey] = useState<string | null>(null)

  const beepedRef = useRef(false)

  const reload = useCallback(async () => {
    // 初回ロード時のみフルスクリーンスピナーを見せる。セット記録直後などの
    // 再フェッチではUIをちらつかせたくないので loading フラグは触らない
    // （初期値 true のまま初回の setLoading(false) で解除される）。
    try {
      const data = await getSession(sessionId)
      setSession(data)
      // テンプレート利用時は items から plan を組む。フリーセッション時は記録された sets から逆算
      if (data.template_id) {
        // 同テンプレートの過去セッションを新しい順に複数取得しておく。
        // 直前1件だけだと、そのセッションを途中で終了して該当種目のセットが無いときに
        // 「前回の記録」「前回のAIアドバイス」が空になってしまうため、種目ごとに一番近い
        // データを持つセッションへフォールバックできるように複数キャッシュする。
        try {
          const sessions = await getSessions(20)
          const candidates = sessions
            .filter((s) => s.template_id === data.template_id && s.id !== sessionId && s.set_count > 0)
            .slice(0, 5)
          const loaded = await Promise.all(candidates.map((c) => getSession(c.id).catch(() => null)))
          setPrevSessions(loaded.filter((s): s is WorkoutSession => s !== null))
        } catch (err) {
          console.warn('過去セッション取得失敗', err)
        }

        try {
          const templates = await getTemplates(true)
          const tpl = templates.find((t) => t.id === data.template_id)
          if (tpl) {
            setPlan(buildPlanFromTemplate(tpl.items))
          } else {
            setPlan(buildPlanFromSets(data.sets))
          }
        } catch (err) {
          console.warn('テンプレート取得失敗', err)
          setPlan(buildPlanFromSets(data.sets))
        }
      } else {
        setPlan(buildPlanFromSets(data.sets))
      }
    } catch (err) {
      console.error(err)
      toast.error('セッション取得失敗')
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    void reload()
  }, [reload])

  // 別のセッションを開いたら前のセット/休憩タイマーを引き継がない（同じセッションへ戻ったときは維持）
  useEffect(() => {
    if (!Number.isFinite(sessionId)) return
    bindSession(sessionId)
  }, [sessionId, bindSession])

  // 250msごとに再レンダリング
  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1)
      setNowMs(Date.now())
    }, 250)
    return () => clearInterval(id)
  }, [])

  // インターバル終了通知
  useEffect(() => {
    if (!restStartedAt || !restTargetSeconds) {
      beepedRef.current = false
      return
    }
    const elapsed = (Date.now() - restStartedAt) / 1000
    if (elapsed >= restTargetSeconds && !beepedRef.current) {
      beepedRef.current = true
      playBeep(300, 880)
      vibrate([200, 100, 200])
    }
  }, [tick, restStartedAt, restTargetSeconds])

  // 各種目の現在のセット番号を計算
  const exerciseProgress = useMemo(() => {
    const progress = new Map<number, number>()
    if (!session) return progress
    for (const s of session.sets) {
      progress.set(s.exercise_id, Math.max(progress.get(s.exercise_id) ?? 0, s.set_index))
    }
    return progress
  }, [session])

  // プラン行ごとの完了セット数。同じ種目が複数行あるときは先行行ぶん (set_offset) を差し引いて割り振る
  const planProgress = useMemo(() => {
    const progress = new Map<string, number>()
    for (const p of plan) {
      const done = (exerciseProgress.get(p.exercise_id) ?? 0) - p.set_offset
      progress.set(p.key, Math.min(Math.max(done, 0), p.target_sets))
    }
    return progress
  }, [plan, exerciseProgress])

  // 未完了の行一覧
  const incompletePlans = useMemo<Plan[]>(
    () => plan.filter((p) => (planProgress.get(p.key) ?? 0) < p.target_sets),
    [plan, planProgress],
  )

  // 完了済み行数（進捗表示用：選択した種目に依存させない）
  const completedExerciseCount = useMemo(
    () => plan.filter((p) => (planProgress.get(p.key) ?? 0) >= p.target_sets).length,
    [plan, planProgress],
  )

  // 現在実行中の行（手動選択を優先し、なければ最初の未完了 plan 項目）
  const currentPlan = useMemo<Plan | null>(() => {
    if (selectedPlanKey !== null) {
      const selected = incompletePlans.find((p) => p.key === selectedPlanKey)
      if (selected) return selected
    }
    return incompletePlans[0] ?? null
  }, [incompletePlans, selectedPlanKey])

  // 画面表示用の行内セット番号
  const currentSetIndex = useMemo(() => {
    if (!currentPlan) return 1
    return (planProgress.get(currentPlan.key) ?? 0) + 1
  }, [currentPlan, planProgress])

  // DB に保存するセット番号は種目内の通し番号
  const nextSetIndex = useMemo(() => {
    if (!currentPlan) return 1
    return (exerciseProgress.get(currentPlan.exercise_id) ?? 0) + 1
  }, [currentPlan, exerciseProgress])

  // 現在種目の前回履歴（同種目の直前セット）
  const lastSet = useMemo<WorkoutSetRecord | null>(() => {
    if (!session || !currentPlan) return null
    const list = session.sets.filter((s) => s.exercise_id === currentPlan.exercise_id)
    return list[list.length - 1] ?? null
  }, [session, currentPlan])

  // 現在種目のセットを含む一番近い過去セッションのセット一覧
  const prevExerciseSets = useMemo<WorkoutSetRecord[]>(() => {
    if (!currentPlan) return []
    for (const s of prevSessions) {
      const sets = s.sets.filter((x) => x.exercise_id === currentPlan.exercise_id)
      if (sets.length > 0) {
        return sets.slice().sort((a, b) => a.set_index - b.set_index)
      }
    }
    return []
  }, [prevSessions, currentPlan])

  // 「前回の記録」の見出しに出す、そのデータを取得した過去セッションの日付
  const prevExerciseSetsDateLabel = useMemo<string | null>(() => {
    if (!currentPlan) return null
    for (const s of prevSessions) {
      if (s.sets.some((x) => x.exercise_id === currentPlan.exercise_id)) {
        return format(parseUtcDate(s.started_at), 'M月d日 (E)', { locale: ja })
      }
    }
    return null
  }, [prevSessions, currentPlan])

  // 現在種目に対するAIアドバイスを持つ一番近い過去セッションのアドバイス
  const prevAiAdvice = useMemo(() => {
    if (!currentPlan) return null
    for (const s of prevSessions) {
      const advice = s.ai_feedback?.per_exercise.find((e) => e.exercise_id === currentPlan.exercise_id)
      if (advice) return advice
    }
    return null
  }, [prevSessions, currentPlan])

  // AI アドバイスを引いてきた過去セッションの日付
  const prevAiAdviceDateLabel = useMemo<string | null>(() => {
    if (!currentPlan) return null
    for (const s of prevSessions) {
      if (s.ai_feedback?.per_exercise.some((e) => e.exercise_id === currentPlan.exercise_id)) {
        return format(parseUtcDate(s.started_at), 'M月d日 (E)', { locale: ja })
      }
    }
    return null
  }, [prevSessions, currentPlan])

  // 種目が変わったら重量推移を取得
  const currentExerciseId = currentPlan?.exercise_id
  useEffect(() => {
    if (!currentExerciseId) return
    let cancelled = false
    getExerciseHistory(currentExerciseId, 20)
      .then((data) => {
        if (!cancelled) setExerciseHistory(data)
      })
      .catch((err) => console.warn('重量推移取得失敗', err))
    return () => {
      cancelled = true
    }
  }, [currentExerciseId])

  // 入力欄のプリセット
  useEffect(() => {
    if (!currentPlan) return
    const last = lastSet
    setWeight(String(last?.weight_kg ?? currentPlan.target_weight_kg ?? ''))
    setReps(String(last?.reps ?? currentPlan.target_reps ?? ''))
  }, [currentPlan, lastSet])

  const setElapsedSec = setStartedAt ? Math.floor((nowMs - setStartedAt) / 1000) : 0
  const restRemainingSec =
    restStartedAt && restTargetSeconds ? restTargetSeconds - Math.floor((nowMs - restStartedAt) / 1000) : 0

  const handleStartSet = () => {
    cancelRest()
    startSet()
  }

  const handleCompleteSet = (failed = false) => {
    if (!currentPlan) return
    if (weight === '') {
      toast.error('重量を入力してください（自重の場合は0）')
      return
    }
    if (reps === '' || Number(reps) <= 0) {
      toast.error('レップ数を入力してください')
      return
    }
    // RPE選択ダイアログを開く
    setPendingFailed(failed)
    setRpeDialogOpen(true)
  }

  const handleRpeSelect = async (selectedRpe: string) => {
    if (!currentPlan) return
    setRpeDialogOpen(false)
    const startedAt = setStartedAt ? new Date(setStartedAt).toISOString() : null
    const completedAtDate = new Date()
    const completedAt = completedAtDate.toISOString()
    const durationSec = setStartedAt ? (completedAtDate.getTime() - setStartedAt) / 1000 : null

    // 今回のセットを記録すると全種目が完了するかを記録前に判定
    const isLastSet = currentSetIndex >= currentPlan.target_sets
    const isAllComplete = isLastSet && incompletePlans.every((p) => p.key === currentPlan.key)

    try {
      await addSet(sessionId, {
        exercise_id: currentPlan.exercise_id,
        set_index: nextSetIndex,
        weight_kg: weight === '' ? null : Number(weight),
        reps: reps === '' ? null : Number(reps),
        rpe: Number(selectedRpe),
        is_failed: pendingFailed,
        started_at: startedAt,
        completed_at: completedAt,
        duration_seconds: durationSec,
      })

      if (isAllComplete) {
        // 全種目完了 → 休憩不要、自動でセッション終了
        cancelRest()
        await reload()
        await autoEndSession()
      } else {
        startRest(currentPlan.rest_seconds)
        await reload()
      }
    } catch (err) {
      console.error(err)
      toast.error('セット記録に失敗しました')
    }
  }

  const autoEndSession = async () => {
    // 終了押下直後に UI を AI 評価画面へ切り替えるため、リクエスト開始前に立てる。
    setEvaluating(true)
    // セット/休憩タイマーが次のセッションに持ち越されないようにここで止める。
    reset()
    try {
      await updateSession(sessionId, { status: 'completed' })
    } catch (err) {
      console.error(err)
      toast.error('終了に失敗しました')
      setEvaluating(false)
      return
    }

    // AI評価を自動実行
    try {
      const updated = await evaluateSession(sessionId)
      setSession(updated)

      // テンプレート使用時: AI の next_plan でテンプレートの重量を更新する。
      // セット数・レップ数・休憩はユーザーが組んだメニューなので書き換えない
      if (updated.template_id && updated.ai_feedback) {
        try {
          const templates = await getTemplates(true)
          const tpl = templates.find((t) => t.id === updated.template_id)
          if (tpl) {
            const updatedItems = tpl.items.map((item) => {
              const aiEx = updated.ai_feedback?.per_exercise.find((e) => e.exercise_id === item.exercise_id)
              return {
                exercise_id: item.exercise_id,
                position: item.position,
                target_sets: item.target_sets,
                target_reps: item.target_reps,
                target_weight_kg: aiEx ? aiEx.next_plan.weight_kg : item.target_weight_kg,
                rest_seconds: item.rest_seconds,
              }
            })
            await updateTemplate(tpl.id, { items: updatedItems })
            toast.success('次回の重量をテンプレートに反映しました')
          }
        } catch (err) {
          console.error('テンプレート更新失敗', err)
          toast.error('テンプレートの自動更新に失敗しました')
        }
      }

      toast.success('AI評価を取得しました')
    } catch (err) {
      console.error(err)
      toast.error('AI評価に失敗しました（セッションは終了済み）')
    } finally {
      setEvaluating(false)
      await reload()
    }
  }

  const handleEndSession = async () => {
    if (!(await confirmDialog({ title: 'セッションを終了しますか？', confirmLabel: '終了' }))) return
    await autoEndSession()
  }

  const handleEvaluate = async () => {
    setEvaluating(true)
    try {
      const updated = await evaluateSession(sessionId)
      setSession(updated)
      toast.success('AI評価を取得しました')
    } catch (err) {
      console.error(err)
      toast.error('AI評価に失敗しました')
    } finally {
      setEvaluating(false)
    }
  }

  if (loading || !session) {
    return (
      <div className="flex flex-col h-screen items-center justify-center">
        <Spinner size={48} />
      </div>
    )
  }

  const isCompleted = session.status === 'completed'
  // 終了ボタン押下後 or 完了済セッションのときは「AI 評価画面」を前面に出し、記録UIを隠す。
  const showEvaluationScreen = isCompleted || evaluating

  return (
    <div className="flex flex-col h-screen">
      <Header title={session.name ?? session.template_name ?? 'セッション'} onClose={() => navigate('/workout')} />
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {evaluating && (
          <Card className="p-4 text-center space-y-2 bg-purple-950/40 border-purple-700">
            <div className="flex items-center justify-center gap-2 text-purple-200">
              <Sparkles className="size-5 animate-pulse" />
              <span className="font-medium">AI が評価中...</span>
            </div>
            <div className="text-xs text-purple-300/70">セッションの内容を分析して次回プランを提案しています</div>
          </Card>
        )}

        {!showEvaluationScreen && currentPlan && (
          <div className="p-2 space-y-3 gap-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">
                  種目 {completedExerciseCount + 1} / {plan.length || '?'}
                </div>
                {!setStartedAt && currentSetIndex === 1 && incompletePlans.length > 1 ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1 text-xl font-bold">
                        {currentPlan.exercise_name}
                        <ChevronDown className="size-5 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {incompletePlans.map((p) => (
                        <DropdownMenuItem key={p.key} onClick={() => setSelectedPlanKey(p.key)}>
                          {p.key === currentPlan.key && <Check className="size-4 mr-1" />}
                          {/* スーパーセットは同じ種目名が並ぶので順番で見分けられるようにする */}
                          {plan.findIndex((x) => x.key === p.key) + 1}. {p.exercise_name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div className="text-xl font-bold">{currentPlan.exercise_name}</div>
                )}
                {currentPlan.exercise_notes && (
                  <div className="text-xs text-muted-foreground mt-0.5">{currentPlan.exercise_notes}</div>
                )}
                <div className="text-xs text-muted-foreground">
                  Set {currentSetIndex} / {currentPlan.target_sets}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground whitespace-nowrap">セッション</div>
                <div className="font-mono text-lg tabular-nums">
                  {formatSeconds(Math.floor((nowMs - parseUtcDate(session.started_at).getTime()) / 1000))}
                </div>
                <div className="text-xs text-muted-foreground mt-1">セット</div>
                <div className="font-mono text-lg tabular-nums">{formatSeconds(setElapsedSec)}</div>
              </div>
            </div>
            {/* ウォームアップ提案 */}
            {currentSetIndex === 1 && (currentPlan.target_weight_kg ?? Number(weight)) > 0 && (
              <div className="rounded border border-amber-700/50 bg-amber-950/30 p-3 space-y-1">
                <span className="text-sm font-medium text-amber-200 flex items-center gap-1">
                  <Dumbbell className="size-3.5" /> ウォームアップ
                </span>
                <div className="text-xs text-amber-300/80">
                  💡 〜{Math.round((currentPlan.target_weight_kg ?? Number(weight)) * 0.2)}kgくらいで軽く1セット
                </div>
              </div>
            )}
            {!isCompleted && restStartedAt && restTargetSeconds && (
              <Card
                className={`p-4 gap-2 ${
                  restRemainingSec < 0 ? 'bg-red-950/40 border-red-700' : 'bg-emerald-950/40 border-emerald-700'
                }`}
              >
                <div className={`text-xs ${restRemainingSec < 0 ? 'text-red-400' : 'text-emerald-400'}`}>休憩中</div>
                <div
                  className={`flex justify-center font-mono text-3xl tabular-nums ${restRemainingSec < 0 ? 'text-red-400' : ''}`}
                >
                  {formatSeconds(restRemainingSec)} / {formatSeconds(restTargetSeconds)}
                </div>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>重量 (kg)</Label>
                <Input type="number" step="0.5" value={weight} onChange={(e) => setWeight(e.target.value)} />
              </div>
              <div>
                <Label>レップ</Label>
                <Input type="number" value={reps} onChange={(e) => setReps(e.target.value)} />
              </div>
            </div>

            <div className="flex gap-2">
              {!setStartedAt ? (
                <Button onClick={handleStartSet} className="flex-1">
                  <Timer className="size-4 mr-1" /> セット開始
                </Button>
              ) : (
                <>
                  <Button onClick={() => handleCompleteSet(false)} className="flex-1">
                    <Check className="size-4 mr-1" /> セット完了
                  </Button>
                  <Button variant="destructive" onClick={() => handleCompleteSet(true)} className="flex-1">
                    <X className="size-4 mr-1" /> 失敗
                  </Button>
                </>
              )}
            </div>

            {exerciseHistory.length >= 2 && <WeightProgressChart data={exerciseHistory} />}
            {prevExerciseSets.length > 0 && (
              <div className="text-xs bg-muted p-2 rounded space-y-0.5">
                <div className="text-muted-foreground font-medium">
                  前回の記録{prevExerciseSetsDateLabel ? ` (${prevExerciseSetsDateLabel})` : ''}
                </div>
                {prevExerciseSets.map((s) => (
                  <div key={s.id} className="text-muted-foreground">
                    Set {s.set_index}: {s.weight_kg ?? '?'}kg × {s.reps ?? '?'}
                    {s.rpe != null ? ` (RPE ${s.rpe})` : ''}
                    {s.is_failed ? ' ❌' : ''}
                  </div>
                ))}
              </div>
            )}

            {prevAiAdvice && (
              <div className="text-xs bg-purple-950/40 border border-purple-700 p-2 rounded space-y-1">
                <div className="text-purple-300 font-medium flex items-center gap-1">
                  <Sparkles className="size-3" /> 前回のAIアドバイス
                  {prevAiAdviceDateLabel ? (
                    <span className="text-purple-300/70 font-normal ml-1">({prevAiAdviceDateLabel})</span>
                  ) : null}
                </div>
                <div className="text-purple-200/80 whitespace-pre-wrap">{prevAiAdvice.evaluation}</div>
                <div className="text-purple-300/60">
                  次回推奨: {prevAiAdvice.next_plan.weight_kg}kg × {prevAiAdvice.next_plan.reps}reps ×{' '}
                  {prevAiAdvice.next_plan.sets}sets
                </div>
              </div>
            )}
          </div>
        )}

        {!showEvaluationScreen && !currentPlan && plan.length > 0 && (
          <Card className="p-4 text-center space-y-2">
            <div className="text-lg font-semibold">全種目完了 🎉</div>
            <Button onClick={handleEndSession} disabled={evaluating} className="w-full">
              セッションを終了
            </Button>
          </Card>
        )}

        {!showEvaluationScreen && plan.length === 0 && (
          <Card className="p-4 space-y-2">
            <div className="text-sm text-muted-foreground">
              プランが空です。テンプレートから開始するか、セットを記録すると自動的に種目が追加されます。
            </div>
          </Card>
        )}

        {isCompleted && !session.ai_feedback && !evaluating && (
          <Button onClick={handleEvaluate} disabled={evaluating} className="w-full">
            <Sparkles className="size-4 mr-1" />
            AI で再評価
          </Button>
        )}

        {isCompleted && session.ai_feedback && (
          <AiEvaluationView evaluation={session.ai_feedback} onNavigateHome={() => navigate('/workout')} />
        )}

        {isCompleted && <MuscleSummaryCard sessionId={session.id} />}

        {/* 記録一覧 */}
        <div className="space-y-2">
          <h3 className="font-semibold">記録</h3>
          {session.sets.length === 0 ? (
            <div className="text-sm text-muted-foreground">まだセットがありません</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(
                session.sets.reduce<Record<number, typeof session.sets>>((acc, s) => {
                  if (!acc[s.exercise_id]) acc[s.exercise_id] = []
                  acc[s.exercise_id].push(s)
                  return acc
                }, {}),
              ).map(([exerciseId, sets]) => (
                <div key={exerciseId} className="space-y-1">
                  <div className="text-xs text-muted-foreground font-medium">{sets[0].exercise_name}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {sets.map((s) => (
                      <span
                        key={s.id}
                        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs tabular-nums ${s.is_failed ? 'border-red-500/50 text-red-400' : 'border-border'}`}
                      >
                        <span className="text-muted-foreground">#{s.set_index}</span>
                        {s.weight_kg ?? '?'}kg×{s.reps ?? '?'}
                        {s.rpe != null ? ` RPE${s.rpe}` : ''}
                        {s.is_failed ? ' ❌' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!showEvaluationScreen && (
          <Button variant="destructive" className="w-full" onClick={handleEndSession}>
            <Square className="size-4 mr-1" /> セッション終了
          </Button>
        )}
      </div>

      {/* RPE選択ダイアログ */}
      <Dialog open={rpeDialogOpen} onOpenChange={setRpeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>RPE（主観的強度）を選択</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {RPE_OPTIONS.map((o) => (
              <Button
                key={o.value}
                variant="outline"
                className="w-full justify-start text-left h-auto py-3"
                onClick={() => handleRpeSelect(o.value)}
              >
                <span className="font-bold mr-2">RPE {o.value}</span>
                <span className="text-muted-foreground">{o.description}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const AiEvaluationView = ({
  evaluation,
  onNavigateHome,
}: {
  evaluation: WorkoutAiEvaluation
  onNavigateHome?: () => void
}) => {
  return (
    <div className="space-y-3">
      <Card className="p-3 bg-purple-950/40 border-purple-700">
        <div className="text-xs text-purple-300 mb-1 flex items-center gap-1">
          <Sparkles className="size-3" /> AI 総評
        </div>
        <div className="text-sm whitespace-pre-wrap">{evaluation.overall_summary}</div>
      </Card>
      {onNavigateHome && (
        <Button variant="outline" className="w-full" onClick={onNavigateHome}>
          <Home className="size-4 mr-1" /> 筋トレ一覧へ戻る
        </Button>
      )}
      <h3 className="font-semibold">次回プラン</h3>
      {evaluation.per_exercise.map((ex) => (
        <Card key={ex.exercise_id} className="p-3 space-y-1">
          <div className="font-medium">{ex.exercise_name}</div>
          <div className="text-sm">{ex.evaluation}</div>
          <div className="text-sm bg-muted p-2 rounded">
            次回: {ex.next_plan.weight_kg}kg × {ex.next_plan.reps}reps × {ex.next_plan.sets}sets ・ rest{' '}
            {ex.next_plan.rest_seconds}s
          </div>
          <div className="text-xs text-muted-foreground">{ex.reasoning}</div>
        </Card>
      ))}
    </div>
  )
}

const WeightProgressChart = ({ data }: { data: ExerciseHistoryPoint[] }) => {
  return (
    <div className="rounded p-2 space-y-1">
      <div className="text-xs text-muted-foreground font-medium">重量推移</div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#a1a1aa' }} tickFormatter={(v: string) => v.slice(5)} />
          <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} domain={['dataMin - 2.5', 'dataMax + 2.5']} />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1c1c1e',
              border: '1px solid #3f3f46',
              borderRadius: 8,
              fontSize: 12,
              color: '#e4e4e7',
            }}
            formatter={(value, name) => [`${value}kg`, name === 'max_weight_kg' ? '最大重量' : name]}
            labelFormatter={(label) => `${label}`}
          />
          <Line
            type="monotone"
            dataKey="max_weight_kg"
            stroke="#818cf8"
            strokeWidth={2}
            dot={{ r: 4, fill: '#818cf8', strokeWidth: 0 }}
            activeDot={{ r: 6, fill: '#a5b4fc' }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
